/** @jsxImportSource @emotion/react */
import { FC, useRef } from 'react';
import styled from '@emotion/styled';

/* ── styles ─────────────────────────────────────────── */
const Wrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;
const Box = styled.input<{
  status?: 'idle' | 'error' | 'success' | undefined;
}>`
  flex: 1;
  min-width: 48px;
  aspect-ratio: 1;
  border-radius: 9.42px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 36px;
  color: #e9ecf5;
  
  background: #23252a;
  outline: none;
  border: ${({ status }) => (status === 'error' ? '2px solid #D65F46' : '1px solid #2B2D33')};
  box-shadow: none;
  box-sizing: border-box;
  padding: 0;
  -webkit-appearance: none;

  &:focus {
    ${({ status }) =>
      status === 'error'
        ? `
          border: 2px solid #D65F46;
          box-shadow: none;
        `
        : `
          border: 2px solid #8097EA;
          box-shadow: 2px 2px 7px 0px #469CFF;
        `}
  }
`;

/* ── component ─────────────────────────────────────── */
interface VerificationCodeInputProps {
  length?: number; // default 6
  value: string; // current code, e.g. "123"
  onChange: (val: string) => void;
  status?: 'idle' | 'error' | 'success';
}

export const VerificationCodeInput: FC<VerificationCodeInputProps> = ({
  length = 6,
  value,
  onChange,
  status,
}) => {
  const refs = useRef<HTMLInputElement[]>([]);

  const setCharAt = (str: string, i: number, c: string) =>
    str.substring(0, i) + c + str.substring(i + 1);

  /* handle typing / replacement */
  const handleChange = (i: number, raw: string) => {
    const digit = raw?.replace(/\D/g, ''); // numeric only
    const next = setCharAt(value.padEnd(length), i, digit); // add or keep padding
    onChange(next.trimEnd());

    if (digit && i < length - 1) refs.current[i + 1]?.focus();
  };

  /* handle navigation & deletion */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === 'Backspace') {
      if (value[i]) {
        // clear current digit
        onChange(setCharAt(value, i, '').trimEnd());
      } else if (i > 0) {
        // move left & clear previous
        refs.current[i - 1]?.focus();
        onChange(setCharAt(value, i - 1, '').trimEnd());
      }
      e.preventDefault();
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  return (
    <Wrapper>
      {Array.from({ length }).map((_, i) => (
        <Box
          key={i}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={1}
          value={value[i] ?? ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          status={status}
          ref={(el) => {
            if (el) refs.current[i] = el;
          }}
        />
      ))}
    </Wrapper>
  );
};
